'use client';
import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Quote } from '../data/quotesInterface';
import QuoteCard from '../components/QuoteCard';
import { Search as SearchIcon, ArrowLeft, SlidersHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import CategoryFilter from '../components/CategoryFilter';
import AuthorFilter from '../components/AuthorFilter';
import { getFilteredQuotes } from '@/lib/quoteAPIHelper';
import { useToast } from '@/hooks/use-toast';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { logAnalyticsEvent } from '@/lib/analytics';
import { useRouter } from 'next/navigation';

const Search = ({initialSearchTerm, initialSearchResults, initialSelectedCategories, initialSelectedAuthors, initialQuotesCount}: {
  initialSearchTerm?: string;
  initialSearchResults?: Quote[];
  initialSelectedCategories?: string[];
  initialSelectedAuthors?: string[];
  initialQuotesCount?: number;
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [searchResults, setSearchResults] = useState<Quote[]>(initialSearchResults || []);
  const [activeTab, setActiveTab] = useState('content');
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // Add filter states similar to home page
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialSelectedCategories || ["All"]);
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>(initialSelectedAuthors || []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [quotesCount, setQuotesCount] = useState(initialQuotesCount || 0);
  const resultsPerPage = 9;

  // Sheet state for filters on mobile
  const [filtersOpen, setFiltersOpen] = useState(false);
  const router = useRouter();

  // Prevent URL change on first page load
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { toast } = useToast();

  // Reset selection when changing tabs
  useEffect(() => {
    if (isFirstLoad) return;
    setSearchTerm('');
    setSearchResults([]);
    setSelectedQuote(null);
    setCurrentPage(1);
    // const params = new URLSearchParams(window.location.search);
    // const categoryFromQuery = params.get('cat');
    // if (categoryFromQuery) {
    //   setSelectedCategories(categoryFromQuery.split(',').map(cat => cat.trim()));
    // }
    // const authorFromQuery = params.get('aut');
    // if (authorFromQuery) {
    //   setSelectedAuthors(authorFromQuery.split(',').map(author => author.trim()));
    // }
    // const searchFromQuery = params.get('sea');
    // if (searchFromQuery) {
    //   setSearchTerm(searchFromQuery);
    // } else {
    //   setSearchTerm('');
    // }
  }, [activeTab]);

  useEffect(() => {
    if (isFirstLoad) return;
    const params = new URLSearchParams(window.location.search);
    params.set('cat', selectedCategories.join(','));
    params.set('aut', selectedAuthors.join(','));
    params.set('sea', searchTerm.toLowerCase());
    router.replace(`${window.location.pathname}?${params.toString()}`);
  }, [selectedCategories, selectedAuthors, searchTerm]);

  const handleSearch = useCallback(async () => {
    if (isFirstLoad) return;
    logAnalyticsEvent('search_click');
    // setSearchResults([]);
    // setIsSearching(true);
    let toastDismiss: (() => void) | null = null;
    const longRequestTimeOutId = setTimeout(() => {
      toastDismiss = toast({
        title: "Loading...",
        description: "This is taking longer than usual. Please wait.",
      }).dismiss;
    }, 1000);
    const results: { quotes: Quote[], totalQuotes: number } = await getFilteredQuotes(selectedCategories, selectedAuthors, searchTerm, currentPage, false);
    clearTimeout(longRequestTimeOutId);
    if (toastDismiss) {
      toastDismiss();
    }
    setQuotesCount(results.totalQuotes);
    setSearchResults(results.quotes);
    // setIsSearching(false);
    setSelectedQuote(null);
    setFiltersOpen(false); // Close filters sheet after search
  }, [activeTab, searchTerm, selectedCategories, selectedAuthors, currentPage]);

  // Update search when filters change
  useEffect(() => {
    handleSearch();
  }, [currentPage, selectedCategories, selectedAuthors]);

  useEffect(() => {
    if (isFirstLoad) return;
    setCurrentPage(1); // Reset to first page on filter change
  }, [selectedCategories, selectedAuthors]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  const handleQuoteSelect = (quote: Quote) => {
    // setSelectedQuote(quote);
    // // Select a random image for the quote
    // setSelectedImage(quote.image);
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
    const quoteUrl = `${siteUrl}/quote/${quote._id}`;
    window.open(quoteUrl, '_blank');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(quotesCount / resultsPerPage);
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;

  // Generate page numbers
  const getPageNumbers = () => {
    const pageNumbers = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Show ellipsis or pages around current
      if (currentPage <= 3) {
        pageNumbers.push(2, 3, 4, '...');
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1);
      } else {
        pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  // Count active filters
  const activeFiltersCount =
    (selectedCategories.length === 1 && selectedCategories[0] === "All" ? 0 : selectedCategories.length) +
    selectedAuthors.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            Search Quotes
          </h1>
        </div>

        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-grow flex gap-2">
              <Input
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-grow"
              />
              <Button
                onClick={handleSearch}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:opacity-90"
              >
                <SearchIcon className="mr-2 h-4 w-4" />
                Search
              </Button>
            </div>

            {/* Filters button for mobile */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="sm:hidden relative border-green-200"
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge
                      className="absolute -top-2 -right-2 bg-green-600 text-white text-xs h-5 w-5 flex items-center justify-center p-0 rounded-full"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Search Filters</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  <CategoryFilter
                    selectedCategories={selectedCategories}
                    onCategoriesChange={setSelectedCategories}
                  />
                  <AuthorFilter
                    selectedAuthors={selectedAuthors}
                    onAuthorsChange={setSelectedAuthors}
                  />
                  <Button
                    onClick={async () => {
                      await handleSearch();
                      setFiltersOpen(false);
                    }}
                    className="w-full mt-4 bg-gradient-to-r from-green-600 to-blue-600 hover:opacity-90"
                  >
                    Apply Filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

        </div>

        {/* Desktop filters panel */}
        <div className="hidden sm:block bg-white/50 rounded-lg shadow-sm p-4 mb-8">
          <div className="flex flex-col md:flex-row md:gap-8">
            <div className="w-full md:w-1/2">
              <CategoryFilter
                selectedCategories={selectedCategories}
                onCategoriesChange={setSelectedCategories}
              />
            </div>
            <div className="w-full md:w-1/2">
              <AuthorFilter
                selectedAuthors={selectedAuthors}
                onAuthorsChange={setSelectedAuthors}
              />
            </div>
          </div>
        </div>

        {/* Active filters badges - mobile only */}
        <div className="sm:hidden mb-4">
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-500">Active filters:</span>
              {selectedCategories.length > 0 && selectedCategories[0] !== "All" && (
                selectedCategories.map(category => (
                  <Badge
                    key={category}
                    variant="secondary"
                    className="bg-green-100 text-green-800"
                  >
                    {category}
                  </Badge>
                ))
              )}
              {selectedAuthors.length > 0 && (
                selectedAuthors.map(author => (
                  <Badge
                    key={author}
                    variant="secondary"
                    className="bg-blue-100 text-blue-800"
                  >
                    {author}
                  </Badge>
                ))
              )}
            </div>
          )}
        </div>

        {searchResults.length > 0 ? (
          <div className="grid gap-6 min-h-[300px]">
            {selectedQuote ? (
              <QuoteCard
                quote={selectedQuote}
                image={selectedImage}
                onRefresh={async () => setSelectedQuote(null)}
              />
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 auto-rows-max">
                  {searchResults.map((quote) => (
                    <Card
                      key={quote._id}
                      className="p-4 cursor-pointer hover:shadow-md transition-all hover:-translate-y-1 hover:bg-white/80 relative overflow-hidden h-fit"
                      onClick={() => handleQuoteSelect(quote)}
                    >
                      <div className="quote-text text-lg mb-3 line-clamp-3 min-h-[72px]">{quote.content}</div>
                      <div className="flex justify-between items-center">
                        <div className="quote-author text-sm text-green-700">— {quote.author}</div>
                        {/* {quote.tags.length > 0 && (
                          <Badge variant="outline" className="text-xs border-green-200">
                            {quote.tags[0]}
                          </Badge>
                        )} */}
                      </div>
                    </Card>
                  ))}
                </div>

              </>
            )}
          </div>
        ) : !isSearching && (searchTerm !== '' || selectedCategories.length > 1 || selectedAuthors.length > 0) ? (
          <div className="text-center py-12 bg-white/30 rounded-lg shadow-sm min-h-[300px] flex items-center justify-center">
            <div>
              <p className="text-lg text-gray-600 mb-2">No quotes found matching your search criteria</p>
              <p className="text-sm text-gray-500">Try different search terms or adjust your filters</p>
            </div>
          </div>
        ) : (
          <div className="text-center bg-white/30 rounded-lg shadow-sm min-h-[300px]">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 h-full">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg bg-white/60 animate-pulse shadow-sm flex flex-col gap-4 h-fit"
                >
                  <div className="h-6 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100 rounded w-3/4 shimmer" />
                  <div className="h-4 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100 rounded w-1/2 shimmer" />
                  <div className="flex gap-2 mt-2">
                    <div className="h-5 w-20 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100 rounded shimmer" />
                    <div className="h-5 w-12 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100 rounded shimmer" />
                  </div>
                </div>
              ))}
              <style jsx>{`
                .shimmer {
                  position: relative;
                  overflow: hidden;
                }
                .shimmer::after {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: -150px;
                  height: 100%;
                  width: 150px;
                  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                  animation: shimmer 1.5s infinite;
                }
                @keyframes shimmer {
                  100% {
                    left: 100%;
                  }
                }
              `}</style>
            </div>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => (
                  <PaginationItem key={index} className="hidden sm:inline-block">
                    {page === '...' ? (
                      <span className="px-4 py-2">&hellip;</span>
                    ) : (
                      <PaginationLink
                        isActive={currentPage === page}
                        onClick={() => typeof page === 'number' && setCurrentPage(page)}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <p className="text-center text-sm text-muted-foreground mt-2">
              Page {currentPage} of {totalPages}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
